import { QueryParamsUpdate } from '@hooks/useQueryParams';

import { useCallback, useEffect, useRef } from 'react';

/**
 * Shared hook for managing table filtering and refresh states across data security components.
 * - AccessComparison, AccessComparisonFields
 * - AccessExplainer, AccessExplainerFields
 * - AccessAudit
 */
export const useTableFiltering = <T extends Record<string, unknown>>(
	originalUpdateParams: (updates: QueryParamsUpdate<T>) => void,
	isRequested: boolean
) => {
	const isFiltering = useRef(false);

	// Wrap the original updateParams to inject filtering state logic
	// When Table's TableModifier calls onChange (filter/sort changes), we set isFiltering=true
	// then delegate to the original function to preserve all useQueryParams behavior
	const updateParams = useCallback((updates: QueryParamsUpdate<T>) => {
		isFiltering.current = true;
		originalUpdateParams(updates);
	}, [ originalUpdateParams ]);

	useEffect(() => {
		if (isFiltering.current && !isRequested) {
			isFiltering.current = false;
		}
	}, [ isFiltering, isRequested ]);

	return {
		isFiltering,
		updateParams
	};
};
